import React from 'react'
import { IApplicationRoute, IRouteProcessor } from './router'

import Login from './pages/Login'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import Dashboard from './pages/Dashboard'
import Tables from './pages/Tables'
import Variables from './pages/Variables'
import VariablesData from './pages/VariablesData'
import EditSpec from './pages/EditSpec'
import Files from './pages/Files'
import ExecutiveReports from './pages/ExecutiveReports'
import AutoCoderPage from './pages/AutoCoderPage'
import AutoCoderShellPage from './pages/AutoCoderShellPage'
import AutoCoderPipelineEditor from './pages/AutoCoderPipelineEditor'
import AutoCoderPipelinePage from './pages/AutoCoderPipelinePage'
import BatchImportPage from './pages/BatchImportPage'
import SavImportPage from './pages/SavImportPage'
import FrameworkLibraryPage from './pages/FrameworkLibraryPage'
import FrameworkEditor from './pages/FrameworkEditor'
import FrameSuggestionsPage from './pages/FrameSuggestionsPage'
import ThemeAITasksPage from './pages/ThemeAITasksPage'
import ThemeAITaskPage from './pages/ThemeAITaskPage'
import Dashboards from './pages/Dashboards'
import DashboardCreatePage from './pages/DashboardCreatePage'
import DashboardBuilderPage from './pages/DashboardBuilderPage'
import DashboardEditor from './pages/DashboardEditor'
import DashboardView from './pages/DashboardView'
import SharedDashboardView from './pages/SharedDashboardView'
import SharedArtifactView from './pages/SharedArtifactView'
import Help from './pages/Help'
import ProgrammeTracker from './pages/ProgrammeTracker'
import Visualizations from './pages/Visualizations'
import VisualizationsReal from './pages/VisualizationsReal'
import TiktokAnalysis from './pages/TiktokAnalysis'
import TiktokAnalytics from './pages/TiktokAnalytics'
import TiktokStart from './pages/TiktokStart'
import TiktokDashboard from './pages/TiktokDashboard'
import SkyProductRoute from './pages/sky-social-media'
import SkyProductSpend from './pages/sky-social-media/ProductSpend'
import PostDataEditor from './pages/sky-social-media/PostDataEditor'
import InfluencerDataPage from './pages/sky-social-media/InfluencerDataPage'
import ReconciliationPage from './pages/sky-social-media/ReconciliationPage'
import { DEFAULT_SKY_PRODUCT } from './pages/sky-social-media/productTabsModel'
import Developer from './pages/Developer'
import AgentPage from './pages/AgentPage'
import GeneralLanding from './pages/GeneralLanding'
import AgentCreatePage from './pages/AgentCreatePage'
import AgentChatPage from './pages/AgentChatPage'
import AgentDesignShowcase from './pages/AgentDesignShowcase'
import AgentArtifactsPage from './pages/AgentArtifactsPage'
import AlertsPage from './pages/AlertsPage'
import AiAccessPage from './pages/AiAccessPage'
import AgentMemoryPage from './pages/AgentMemoryPage'
import MySubscriptionsPage from './pages/MySubscriptionsPage'
import AlertDetailPage from './pages/AlertDetailPage'
import GuidedListPage from './pages/GuidedListPage'
import GuidedConversationPage from './pages/GuidedConversationPage'
import GuidedAgendaPage from './pages/GuidedAgendaPage'
import AdminGuideAgenda from './pages/admin/AdminGuideAgenda'
import BatchGenerate from './pages/BatchGenerate'
import BatchRegenerate from './pages/BatchRegenerate'

const Admin = React.lazy(() => import('./pages/admin/Layout'))

const requireUser: IRouteProcessor = (user) => {
  if (user) return
  return {
    name: 'home',
    params: {},
  }
}

/** Managers only. A logged-in non-manager is sent to the customer's agent
 *  start screen rather than the login page, which would read as "logged out".
 *  A route with no customer in its path (my-subscriptions) uses the session's
 *  first customer: a 'home' redirect is skipped while the app first loads, so
 *  a pasted URL would otherwise open (browser walkthrough LIVE-UI-01). 'home'
 *  only when the session has no customer at all. */
const requireManager: IRouteProcessor = (user, _fromState, toState) => {
  if (!user) return { name: 'home', params: {} }
  if (user.role_permissions?.isManager) return
  const customer = toState.params?.customer || user.session_info?.sessionCusts?.find((c) => c?.name)?.name
  if (!customer) return { name: 'home', params: {} }
  return { name: 'agent', params: { customer } }
}

export const DEFAULT_ROUTE: IApplicationRoute = {
  name: 'home',
  path: '/',
  meta: {
    title: 'Login',
    public: true,
  },
  render: () => <Login />,
}

const routes: IApplicationRoute[] = [DEFAULT_ROUTE, {
  name: 'forgot-password',
  path: '/forgot-password',
  meta: {
    title: 'Forgot Password',
    public: true,
  },
  render: () => <ForgotPassword />,
}, {
  name: 'reset-password',
  path: '/reset-password',
  meta: {
    title: 'Reset Password',
    public: true,
  },
  render: () => <ResetPassword />,
}, {
  name: 'shared-dashboard',
  path: '/dashboard/:shareId',
  meta: {
    title: 'Dashboard',
    public: true,
    hideCustomerSelect: true,
    hideJobSelect: true,
    hideTopBar: true,
  },
  render: () => <SharedDashboardView />,
}, {
  // The public help (System context, 13 Oct 2026): every 'everyone' section
  // of the feature registry, shipped text only, no login, drawn as the
  // Platinum docs on bayesprice.com/docs. /help is the overview and
  // /help/<section> one section. Both are listed in App.tsx's publicRoutes,
  // which is the real gate. The page draws its own header, nav and main
  // landmarks, so Layout does not wrap it in a <main> (ownLandmarks).
  name: 'help',
  path: '/help',
  meta: {
    title: 'Help',
    public: true,
    hideCustomerSelect: true,
    hideJobSelect: true,
    hideTopBar: true,
    ownLandmarks: true,
  },
  render: () => <Help />,
}, {
  name: 'help-section',
  path: '/help/:section',
  meta: {
    title: 'Help',
    public: true,
    hideCustomerSelect: true,
    hideJobSelect: true,
    hideTopBar: true,
    ownLandmarks: true,
  },
  render: () => <Help />,
}, {
  // A shared artifact by link (Adam, 13 Sep 2026): no login, like a shared
  // dashboard. Also listed in App.tsx's publicRoutes, which is the real gate.
  name: 'shared-artifact',
  path: '/artifact/:shareId',
  meta: {
    title: 'Shared file',
    public: true,
    hideCustomerSelect: true,
    hideJobSelect: true,
    hideTopBar: true,
  },
  render: () => <SharedArtifactView />,
}, {
  name: 'landing',
  path: '/landing/:customer',
  meta: {
    title: 'Home',
  },
  processRoute: requireUser,
  render: () => <GeneralLanding />,
}, {
  name: 'dashboard',
  path: '/dashboard/:customer/:job',
  meta: {
    title: 'Dashboard',
  },
  processRoute: requireUser,
  render: () => <Dashboard />,
}, {
  name: 'tables',
  path: '/tables/:customer/:job',
  meta: {
    title: 'Tables',
  },
  processRoute: requireUser,
  render: () => <Tables />,
}, {
  name: 'multitables',
  path: '/multitables/:customer/:job/:multiTableId',
  meta: {
    title: 'Multi-Tables',
  },
  processRoute: requireUser,
  render: () => <Tables />,
}, {
  // Variables opens on the respondent data view (Adam and Matt, 20 Oct;
  // #1887, #1914). It follows agent access: the level comes from /me, not the
  // login token, so the route admits any signed-in user and the page shows
  // someone below agent access the reason in place once /me answers
  // (useDataViewAccess); the server refuses too.
  name: 'variables',
  path: '/variables/:customer/:job',
  meta: {
    title: 'Variables',
  },
  processRoute: requireUser,
  render: () => <VariablesData />,
}, {
  // The data view's address before 20 Oct, kept so a shared link still opens.
  name: 'variables-data',
  path: '/variables/:customer/:job/data',
  meta: {
    title: 'Data view',
  },
  processRoute: requireUser,
  render: () => <VariablesData />,
}, {
  // The variable tree with its frequencies, constructs, Edit Variable and Edit
  // Vartree. Until the construct tools' UI pass (#1889) only Admin →
  // Management → Variable tools links here, for Managers; its own gates are
  // unchanged (constructs any member, edits Managers).
  name: 'variables-tools',
  path: '/variables/:customer/:job/tools',
  meta: {
    title: 'Variables',
  },
  processRoute: requireUser,
  render: () => <Variables />,
}, {
  name: 'editspec',
  path: '/editspec/:customer/:job',
  meta: {
    title: 'Edit Spec',
  },
  processRoute: requireUser,
  render: () => <EditSpec />,
}, {
  name: 'batch-generate',
  path: '/batch-generate/:customer/:job',
  meta: {
    title: 'Batch Generate Tables',
  },
  processRoute: requireUser,
  render: () => <BatchGenerate />,
}, {
  name: 'batch-regenerate',
  path: '/batch-regenerate/:customer/:job',
  meta: {
    title: 'Regenerate Tables',
  },
  processRoute: requireUser,
  render: () => <BatchRegenerate />,
}, {
  name: 'files',
  path: '/files/:customer/:job',
  meta: {
    title: 'Files',
  },
  processRoute: requireUser,
  render: () => <Files />,
}, {
  name: 'admin',
  path: '/admin/:customer',
  meta: {
    title: 'Admin',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-users',
  path: '/admin/:customer/users',
  meta: {
    title: 'Admin - User Management',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-jobs',
  path: '/admin/:customer/jobs',
  meta: {
    title: 'Admin - Job Management',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // Customers (DNA replacement T5, T6): create a customer, edit its licences. Manager only.
  name: 'admin-customers',
  path: '/admin/:customer/customers',
  meta: {
    title: 'Admin - Customers',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-context',
  path: '/admin/:customer/context',
  meta: {
    title: 'Admin - Context',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-personas',
  path: '/admin/:customer/personas',
  meta: {
    title: 'Admin - Personas',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-settings',
  path: '/admin/:customer/settings',
  meta: {
    title: 'Admin - Settings',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-azure-costs',
  path: '/admin/:customer/azure-costs',
  meta: {
    title: 'Admin - Azure Costs',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-llm-usage',
  path: '/admin/:customer/llm-usage',
  meta: {
    title: 'Admin - LLM Usage',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // The old Token Usage path, kept so existing links and bookmarks land
  // somewhere sensible. It renders the same page; only the name changed, to
  // stop implying the report is only about tokens.
  name: 'admin-token-usage',
  path: '/admin/:customer/token-usage',
  meta: {
    title: 'Admin - LLM Usage',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-autocoder-throughput',
  path: '/admin/:customer/autocoder-throughput',
  meta: {
    title: 'Admin - AutoCoder Throughput',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-models',
  path: '/admin/:customer/models',
  meta: {
    title: 'Admin - Models',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-activity',
  path: '/admin/:customer/activity',
  meta: {
    title: 'Admin - User Activity',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-tasks',
  path: '/admin/:customer/tasks',
  meta: {
    title: 'Admin - Task Queue',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-carbon-cache',
  path: '/admin/:customer/carbon-cache',
  meta: {
    title: 'Admin - Carbon Cache',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-search',
  path: '/admin/:customer/search',
  meta: {
    title: 'Admin - Search Index',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-prompts',
  path: '/admin/:customer/prompts',
  meta: {
    title: 'Admin - Prompts',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-help-tours',
  path: '/admin/:customer/help-tours',
  meta: {
    title: 'Admin - Help & tours',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // Walkthroughs (20 Oct 2026): every walkthrough file (help/walkthroughs),
  // with Start and Copy link. Manager only; the admin Layout gates it too.
  name: 'admin-walkthroughs',
  path: '/admin/:customer/walkthroughs',
  // The job select stays: Start and Copy link use the header's job.
  meta: {
    title: 'Admin - Walkthroughs',
  },
  processRoute: requireManager,
  render: () => <Admin />,
}, {
  // Demos (design/2026-10-22-chief-of-staff-and-demos.md T19): demo stories,
  // the demo-customer mark, Reset and steps. Manager only; the admin Layout
  // and the server gate it too.
  name: 'admin-demos',
  path: '/admin/:customer/demos',
  meta: {
    title: 'Admin - Demos',
    hideJobSelect: true,
  },
  processRoute: requireManager,
  render: () => <Admin />,
}, {
  // Guided conversations (28 Oct 2026): every guide of the customer, and one
  // guide's agenda, invitations and responses. A Manager, or a reviewer a
  // Manager named on a guide (migration 000190): the admin Layout
  // (isManagerOrGuideReviewer) and the server gate them.
  name: 'admin-guides',
  path: '/admin/:customer/guides',
  meta: {
    title: 'Admin - Guided conversations',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-guide',
  path: '/admin/:customer/guides/:guideId',
  meta: {
    title: 'Admin - Guide',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // A guide's agenda as a printable page (window.print), outside the admin
  // layout so the printout is the agenda alone. A Manager or the guide's reviewers; the server gates it.
  name: 'admin-guide-agenda',
  path: '/admin/:customer/guides/:guideId/agenda',
  meta: {
    title: 'Guide agenda',
    hideJobSelect: true,
    hideTopBar: true,
  },
  processRoute: requireUser,
  render: () => <AdminGuideAgenda />,
}, {
  // System context (13 Oct 2026): a Manager's note per feature of the
  // registry (help/features). Manager only; the admin Layout gates it too.
  name: 'admin-system-context',
  path: '/admin/:customer/system-context',
  meta: {
    title: 'Admin - System context',
    hideJobSelect: true,
  },
  processRoute: requireManager,
  render: () => <Admin />,
}, {
  name: 'admin-teams',
  path: '/admin/:customer/teams',
  meta: {
    title: 'Admin - Teams',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-ai-budgets',
  path: '/admin/:customer/ai-budgets',
  meta: {
    title: 'Admin - AI budgets',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-ai-budget-team',
  path: '/admin/:customer/ai-budgets/:teamId',
  meta: {
    title: 'Admin - AI budgets',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-ai-usage',
  path: '/admin/:customer/ai-usage',
  meta: {
    title: 'Admin - AI usage',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-usage',
  path: '/admin/:customer/usage',
  meta: {
    title: 'Admin - Usage',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // AI models in use (#1234): Realm admins and Managers read it.
  name: 'admin-ai-models',
  path: '/admin/:customer/ai-models',
  meta: {
    title: 'Admin - AI models in use',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-stats-review',
  // ?from=&to= (epoch ms) preset the date range, like ?editStory on the
  // session route: read from router.params, not declared in the path.
  path: '/admin/:customer/stats-review',
  meta: {
    title: 'Admin - Stats review',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-usage-team',
  path: '/admin/:customer/usage/:teamId',
  meta: {
    title: 'Admin - Usage',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-billing-price-list',
  path: '/admin/:customer/billing/price-list',
  meta: {
    title: 'Admin - Price list',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  // Not under /ai-budgets/: /admin/:customer/ai-budgets/:teamId would swallow it.
  name: 'admin-billing-simulator',
  path: '/admin/:customer/billing/simulator',
  meta: {
    title: 'Admin - Billing simulator',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-billing-margin',
  path: '/admin/:customer/billing/margin',
  meta: {
    title: 'Admin - Margin',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-sessions',
  path: '/admin/:customer/sessions',
  meta: {
    title: 'Admin - Agent Sessions',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-sql',
  path: '/admin/:customer/sql',
  meta: {
    title: 'Admin - SQL Query',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-password',
  path: '/admin/:customer/password',
  meta: {
    title: 'Admin - Change Password',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-cache',
  path: '/admin/:customer/cache',
  meta: {
    title: 'Admin - Cache Management',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-brand-themes',
  path: '/admin/:customer/brand-themes',
  meta: {
    title: 'Admin - Brand Themes',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-data-connections',
  path: '/admin/:customer/data-connections',
  meta: {
    title: 'Admin - Data connections',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-data-connection',
  path: '/admin/:customer/data-connections/:connectionId',
  meta: {
    title: 'Admin - Data connection',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-social-accounts',
  path: '/admin/:customer/social-accounts',
  meta: {
    title: 'Admin - Social accounts',
    hideJobSelect: true,
  },
  processRoute: requireManager,
  render: () => <Admin />,
}, {
  name: 'admin-social-posts',
  path: '/admin/:customer/social-posts',
  meta: {
    title: 'Admin - Social posts',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-notifications',
  path: '/admin/:customer/notifications',
  meta: {
    title: 'Admin - Notifications',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-monitoring',
  path: '/admin/:customer/monitoring',
  meta: {
    title: 'Admin - System Monitoring',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-agent-skills',
  path: '/admin/:customer/agent-skills',
  meta: {
    title: 'Admin - Skills',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-share-views',
  path: '/admin/:customer/share-views',
  meta: {
    title: 'Admin - Dashboard Share Views',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-media-library',
  path: '/admin/:customer/media-library',
  meta: {
    title: 'Admin - Media Library',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-programme-trackers',
  path: '/admin/:customer/programme-trackers',
  meta: {
    title: 'Admin - Programme Trackers',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'admin-programme-tracker-edit',
  path: '/admin/:customer/programme-trackers/:id',
  meta: {
    title: 'Admin - Programme Tracker',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Admin />,
}, {
  name: 'executive',
  path: '/executive/:customer/:job',
  meta: {
    title: 'Executive Reports',
  },
  processRoute: requireUser,
  render: () => <ExecutiveReports />,
}, {
  name: 'dashboards',
  path: '/dashboards/:customer',
  meta: {
    // No hideJobSelect: the list is job-scoped off account.job (#962), so the
    // global Job pill is the only way to change job here. See routes.test.ts.
    // Title is "DataViz", as the nav says, even though the route name/path
    // stay `dashboards` — see docs/dashboards.md.
    title: 'DataViz',
  },
  processRoute: requireUser,
  render: () => <Dashboards />,
}, {
  // "+ New Dashboard" entry point (#971 Phase 1): three converging on-ramps
  // (build it yourself / start from a template / agent — Phase 3, disabled)
  // before landing in the layout builder below.
  name: 'dashboard-create',
  path: '/dashboards/:customer/create',
  meta: {
    title: 'New Dashboard',
    lockJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <DashboardCreatePage />,
}, {
  // The layout builder canvas every creation path converges on. `name` and
  // (optionally) `template` arrive as query params from dashboard-create —
  // queryParamsMode is 'loose', so they don't need to appear in `path`.
  name: 'dashboard-builder',
  path: '/dashboards/:customer/builder',
  meta: {
    title: 'Layout Builder',
    lockJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <DashboardBuilderPage />,
}, {
  name: 'programme-tracker',
  path: '/projects/:slug',
  meta: {
    title: 'Programme Tracker',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <ProgrammeTracker />,
}, {
  name: 'dashboard-editor',
  path: '/dashboards/:customer/:dashboardId/edit',
  meta: {
    title: 'Dashboard Editor',
    lockJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <DashboardEditor />,
}, {
  name: 'dashboard-view',
  path: '/dashboards/:customer/:dashboardId/view',
  meta: {
    title: 'Dashboard View',
    lockJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <DashboardView />,
}, {
  // The AutoCoder shell: Data / Pipelines / Code Frames / Activity, selected
  // by `?tab=` and defaulting to Pipelines so existing links are unchanged.
  name: 'autocoder',
  path: '/autocoder/:customer/:job',
  meta: {
    title: 'AutoCoder',
    // `hideAssistant` keeps Douglas off the working pages (Kai, 2026-08-14).
    // It is set per route rather than by matching path prefixes in Layout so
    // the page that wants the mic gone is the page that says so — every
    // /autocoder/* and /sky-social-media/* route below carries it too.
    hideAssistant: true,
  },
  // The AutoCoder pipeline admin is Manager-only, on the server too (access
  // audit T11, Matt 30 Sep 2026). The Social Media dashboards below stay
  // requireUser.
  processRoute: requireManager,
  render: () => <AutoCoderShellPage />,
}, {
  // The pipeline's own page (left-hand nav, Throughput landing). This owns the
  // bare `/pipelines/:pipelineId` path; the wizard/editor moved down to
  // `/edit` so the two patterns cannot collide in the matcher.
  name: 'autocoder-pipeline',
  path: '/autocoder/:customer/:job/pipelines/:pipelineId',
  meta: {
    title: 'AutoCoder Pipeline',
    hideAssistant: true,
  },
  processRoute: requireManager,
  render: () => <AutoCoderPipelinePage />,
}, {
  name: 'autocoder-pipeline-edit',
  path: '/autocoder/:customer/:job/pipelines/:pipelineId/edit',
  meta: {
    title: 'Edit AutoCoder Pipeline',
    hideAssistant: true,
  },
  processRoute: requireManager,
  render: () => <AutoCoderPipelineEditor />,
}, {
  name: 'batch-import',
  path: '/autocoder/:customer/:job/batch-import',
  meta: {
    title: 'Batch Import',
    hideAssistant: true,
  },
  processRoute: requireManager,
  render: () => <BatchImportPage />,
}, {
  name: 'sav-import',
  path: '/import-sav/:customer',
  meta: {
    title: 'Import Variables',
    hideJobSelect: true,
  },
  // Matches its server group, /job/:customer/import-sav/* (RequireManager,
  // job_sav_import.go) — audit A07-15.
  processRoute: requireManager,
  render: () => <SavImportPage />,
}, {
  name: 'themeai',
  path: '/theme-ai/:customer/:job',
  meta: {
    title: 'Theme AI',
  },
  processRoute: requireUser,
  render: () => (
    <AutoCoderPage
      title="Theme AI"
      description="Manage Theme AI imports for this customer and job."
      newButtonText="New Theme AI Import"
      defaultLabels={{ type: 'themeai' }}
      newPipelineLabels="type:themeai"
    />
  ),
}, {
  name: 'theme-ai-tasks',
  path: '/theme-ai-tasks/:customer/:job',
  meta: {
    title: 'Theme AI Tasks',
  },
  processRoute: requireUser,
  render: () => <ThemeAITasksPage />,
}, {
  name: 'theme-ai-task',
  path: '/theme-ai-task/:customer/:job/:taskId',
  meta: {
    title: 'Theme AI Task',
  },
  processRoute: requireUser,
  render: () => <ThemeAITaskPage />,
}, {
  // Forwards into the shell's Code Frames tab (see FrameworkLibraryPage).
  name: 'frameworks',
  path: '/frameworks/:customer/:job',
  meta: {
    title: 'Code Frame Library',
  },
  processRoute: requireManager,
  render: () => <FrameworkLibraryPage />,
}, {
  // A dynamic code frame's suggestions and their moderation
  // (docs/autocoder-dynamic-frames.md). Before the editor's route.
  name: 'framework-suggestions',
  path: '/frameworks/:customer/:job/:frameworkId/suggestions',
  meta: {
    title: 'Code Frame Suggestions',
  },
  processRoute: requireManager,
  render: () => <FrameSuggestionsPage />,
}, {
  name: 'framework-editor',
  path: '/frameworks/:customer/:job/:frameworkId',
  meta: {
    title: 'Code Frame Editor',
  },
  processRoute: requireManager,
  render: () => <FrameworkEditor />,
}, {
  name: 'visualizations',
  path: '/visualizations/:customer/:job',
  meta: {
    title: 'Visualizations',
  },
  render: () => <Visualizations />,
}, {
  name: 'visualizations-real',
  path: '/visualizations-real/:customer/:job',
  meta: {
    title: 'Women\'s Football Visualizations',
  },
  render: () => <VisualizationsReal />,
}, {
  name: 'tiktok-comments',
  path: '/tiktok/:customer/:job',
  meta: {
    title: 'TikTok Comment Analysis',
  },
  processRoute: requireUser,
  render: () => <TiktokAnalysis />,
}, {
  name: 'tiktok-analytics',
  path: '/tiktok-analytics/:customer/:job',
  meta: {
    title: 'TikTok Analytics Dashboard',
  },
  processRoute: requireUser,
  render: () => <TiktokAnalytics />,
}, {
  name: 'tiktok-start',
  path: '/tiktok-start/:customer/:job',
  meta: {
    title: 'TikTok Analytics',
  },
  processRoute: requireUser,
  render: () => <TiktokStart />,
}, {
 name: 'tiktok-dashboard',
 path: '/tiktok-dashboard/:customer/:job',
 meta: {
   title: 'TikTok Dashboard',
 },
 processRoute: requireUser,
 render: () => <TiktokDashboard />,
}, {
  // Sky's product dashboards. NO :customer and NO :job — this is bespoke work
  // for one customer and the URL should say so rather than presenting itself as
  // one instance of a suite. The backend group fixes both server-side; the
  // frontend only decides whether to offer the menu entry at all
  // (constants/skySocialMedia.ts).
  name: 'sky-social-media',
  path: '/sky-social-media',
  meta: {
    title: 'Social Media',
    hideAssistant: true,
  },
  // THE CHOOSER IS GONE (Matt, 2026-08-17). Departments are a tab strip on the
  // dashboards themselves, so this bare path is no longer a page — it redirects
  // to the default department. A redirect rather than "render Brand here"
  // deliberately: the URL then names the department actually on screen, so it
  // bookmarks, shares and highlights its own tab correctly.
  processRoute: (user, fromState, toState) => (
    requireUser(user, fromState, toState) ?? {
      name: 'sky-social-media-product',
      params: { product: DEFAULT_SKY_PRODUCT },
    }
  ),
  render: () => <SkyProductRoute />,
}, {
  // Find one post by its link and correct what it says (#1196). NOT a
  // department page: `/entities/query`, `GET /entities/:id` and its PATCH are
  // registered only on the job-wide AutoCoder group, and the work suits that —
  // which department a post belongs to is an answer this page gives you, not a
  // precondition for pasting a link.
  //
  // 🔴 IT IS A SIBLING OF `/:product` AND MUST NOT BE READ AS ONE. route-node
  // sorts siblings with the same segment count by URL-PARAM COUNT ASCENDING, so
  // this static path is matched before the parameterised one wherever the two
  // sit in this file. Declared above it anyway, so a reader does not have to
  // know that rule to believe the ordering. If it ever regressed the symptom is
  // loud rather than silent — SkyProductRoute renders "There is no dashboard
  // called entities" rather than quietly showing a department.
  name: 'sky-social-media-entities',
  path: '/sky-social-media/entities',
  meta: {
    title: 'Post data',
    hideAssistant: true,
  },
  processRoute: requireUser,
  render: () => <PostDataEditor />,
}, {
  // Upload Sky's influencer export (#1694 follow-up), the Admin tab's second
  // view beside Post data. Admins only on the server. A static sibling of
  // `/:product`, declared above it for the reason the Post data route gives.
  name: 'sky-social-media-influencer-data',
  path: '/sky-social-media/influencer-data',
  meta: {
    title: 'Influencer data',
    hideAssistant: true,
  },
  processRoute: requireUser,
  render: () => <InfluencerDataPage />,
}, {
  // How the dashboards' numbers relate to Sky's own Sprinklr table, post by
  // post, and the two counting switches (Kai, 16 Oct 2026). Every Sky viewer
  // may open it; the server narrows it to the departments they may open. A
  // static sibling of `/:product`, declared above it for the reason the Post
  // data route gives.
  name: 'sky-social-media-reconciliation',
  path: '/sky-social-media/reconciliation',
  meta: {
    title: 'Reconciliation',
    hideAssistant: true,
  },
  processRoute: requireUser,
  render: () => <ReconciliationPage />,
}, {
  name: 'sky-social-media-product',
  path: '/sky-social-media/:product',
  meta: {
    title: 'Social Media',
    hideAssistant: true,
  },
  processRoute: requireUser,
  // Resolves the slug to one of the five product pages. An unknown slug renders
  // a message rather than redirecting to the chooser — a bookmarked URL that
  // silently lands on a different department is worse than one that says it is
  // wrong.
  render: () => <SkyProductRoute />,
}, {
  // One department's own token spend (T16's endpoint). Same no-:customer,
  // no-:job shape as the routes above — the backend group fixes both.
  name: 'sky-social-media-product-spend',
  path: '/sky-social-media/:product/spend',
  meta: {
    title: 'Social Media',
    hideAssistant: true,
  },
  processRoute: requireUser,
  render: () => <SkyProductSpend />,
}, {
  name: 'agent-new',
  // ?alert=new (a new alert) or ?alert=<id> (change that alert) puts the
  // alert card above the composer (alerts round, 12 Oct): read from
  // router.params, not declared in the path, like ?editStory.
  path: '/agent/:customer/new',
  meta: {
    title: 'Agent',
    // The header's job picker stays: it is how a user sees, and changes, the
    // job the session will start on (Matt, 11 Sep, #1347: a session started on
    // Protect when Prius was meant, because nothing on screen said which).
  },
  processRoute: requireUser,
  render: () => <AgentCreatePage />,
}, {
  name: 'agent-artifacts',
  path: '/agent/:customer/:sessionId/artifacts',
  meta: {
    title: 'Agent Artifacts',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <AgentArtifactsPage />,
}, {
  // Every message shape on one page, from fixed data — for judging a visual
  // change to the conversation column. Must sit ABOVE agent-session, whose
  // :sessionId would otherwise swallow "design".
  name: 'agent-design',
  path: '/agent/:customer/design',
  meta: {
    title: 'Agent Design Showcase',
    hideJobSelect: true,
  },
  // A design tool, not a customer surface: managers only (Adam, 11 Sep).
  processRoute: requireManager,
  render: () => <AgentDesignShowcase />,
}, {
  name: 'agent-session',
  path: '/agent/:customer/:sessionId',
  meta: {
    title: 'Agent',
    // Read-only pill showing the SESSION's job(s), published by the page the
    // same way a dashboard publishes its own (#1347): the session owns its
    // job set, and the Jobs tab is where it changes.
    lockJobSelect: true,
    lockJobOwner: 'session',
  },
  processRoute: requireUser,
  render: () => <AgentChatPage />,
}, {
  name: 'agent',
  path: '/agent/:customer',
  meta: {
    title: 'Agent',
    hideJobSelect: true,
  },
  processRoute: (user, fromState, toState) => {
    if (!user) return { name: 'home', params: {} }
    return { name: 'agent-new', params: { customer: toState.params.customer } }
  },
  render: () => <AgentPage />,
}, {
  name: 'alerts',
  path: '/alerts/:customer',
  meta: {
    title: 'Alerts',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <AlertsPage />,
}, {
  name: 'alert-detail',
  path: '/alerts/:customer/:alertId',
  meta: {
    title: 'Alert Detail',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <AlertDetailPage />,
}, {
  // Guided conversations (28 Oct 2026; design/2026-10-28-guided-conversations.md):
  // the signed-in person's invitations, one conversation (a `guided` agent
  // chat beside the agenda tracker and the notes) and its agenda to print.
  // The server answers 404 to anyone who is neither the invitee nor a Manager.
  name: 'guided',
  path: '/guided/:customer',
  meta: {
    title: 'Guided conversations',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <GuidedListPage />,
}, {
  name: 'guided-response',
  path: '/guided/:customer/:responseId',
  meta: {
    title: 'Guided conversation',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <GuidedConversationPage />,
}, {
  name: 'guided-agenda',
  path: '/guided/:customer/:responseId/agenda',
  meta: {
    title: 'Agenda',
    hideJobSelect: true,
    hideTopBar: true,
  },
  processRoute: requireUser,
  render: () => <GuidedAgendaPage />,
}, {
  // "Your AI access" (#1537 PR D, issue #1293) — any signed-in customer
  // user's own level, every level side by side with what it adds, and
  // Request access.
  name: 'ai-access',
  path: '/ai-access/:customer',
  meta: {
    title: 'Your AI access',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <AiAccessPage />,
}, {
  // "What Claude remembers about you" (agent runner plan M4, item 5): any
  // signed-in user's own memory notes for this customer, read-only.
  name: 'agent-memory',
  path: '/agent-memory/:customer',
  meta: {
    title: 'What Claude remembers about you',
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <AgentMemoryPage />,
}, {
  // "My subscriptions" (#964 v1) — every dashboard the current user watches,
  // active + tombstoned, with unsubscribe toggles. Scoped off the account's
  // selected customer, not a URL param — mirrors 'dashboards'.
  name: 'my-subscriptions',
  path: '/my-subscriptions',
  meta: {
    title: 'My Subscriptions',
    hideJobSelect: true,
  },
  // Manager-only since 30 Sep 2026 (access audit T11): reached from the admin
  // menu's Management group; GET /notifications/subscriptions is
  // RequireManager on the server.
  processRoute: requireManager,
  render: () => <MySubscriptionsPage />,
}, {
  name: 'developer',
  path: '/developer',
  meta: {
    title: 'Developer Tools',
    hideCustomerSelect: true,
    hideJobSelect: true,
  },
  processRoute: requireUser,
  render: () => <Developer />,
}]

export default routes
